// Questionario pre-analisi cute — INTERVENTO 3 / passo 3a.
// Solo raccolta dati. Non è ancora collegato al motore di raccomandazione.
// Tutte le domande sono opzionali: l'analisi parte anche senza risposte.
//
// Domande ed enum allineati 1:1 a Trico_survay.py (ScoreEngine/TricoProtocol/
// pipeline/TricoProtocol/Trico_survay.py) — stessi id/valori dei 12 campi
// *_answer consumati da Trico_survay.convert(). Testi in italiano ripresi
// da ScoreEngine/static/AITricoProtocol.html (solo i testi, non il layout).
//
// Convenzione: il componente è controllato. Il parent gestisce lo state
// (`value`) e riceve gli aggiornamenti via `onChange(nextValue)`.
// `value` è un oggetto piatto del tipo:
//   { scalp_oiliness_answer: "oily", hair_loss_answer: "yes_slightly", ... }
// I campi non risposti restano assenti dall'oggetto (mai null/undefined
// in chiaro, così model_dump(exclude_none=True) lato backend resta pulito).

import React, { useState } from "react";
import { ChevronDown, ChevronRight, ClipboardList } from "lucide-react";

const BLOCKS = [
  {
    title: "Cute",
    questions: [
      {
        id: "scalp_oiliness_answer",
        label: "Quanto tende a essere grasso il tuo cuoio capelluto?",
        options: [
          { value: "normal",   label: "Normale" },
          { value: "oily",     label: "Grasso" },
          { value: "veryOily", label: "Molto grasso" },
        ],
      },
      {
        id: "scalp_hydration_answer",
        label: "Come valuti l'idratazione del cuoio capelluto?",
        options: [
          { value: "normal",  label: "Normale" },
          { value: "dry",     label: "Secco" },
          { value: "veryDry", label: "Molto secco" },
        ],
      },
      {
        id: "scalp_sensitivity_answer",
        label: "Il cuoio capelluto tende ad essere sensibile / irritato?",
        options: [
          { value: "no",  label: "No" },
          { value: "yes", label: "Sì" },
        ],
      },
      {
        id: "scalp_flaking_answer",
        label: "Noti presenza di forfora / scaglie?",
        options: [
          { value: "no",           label: "No" },
          { value: "yes_slightly", label: "Sì leggermente" },
          { value: "yes_a_lot",    label: "Sì molto" },
        ],
      },
    ],
  },
  {
    title: "Capelli",
    questions: [
      {
        id: "hair_strength_answer",
        label: "Come valuti la resistenza dei capelli?",
        options: [
          { value: "normal",   label: "Normale" },
          { value: "weak",     label: "Debole" },
          { value: "veryWeak", label: "Molto debole" },
        ],
      },
      {
        id: "hair_volume_answer",
        label: "Come valuti il volume dei capelli?",
        options: [
          { value: "normal", label: "Normale" },
          { value: "low",    label: "Basso" },
          { value: "veryLow", label: "Molto basso" },
        ],
      },
      {
        id: "hair_loss_answer",
        label: "Noti una costante caduta dei capelli?",
        options: [
          { value: "no",           label: "No" },
          { value: "yes_slightly", label: "Sì leggermente" },
          { value: "yes_a_lot",    label: "Sì molto" },
        ],
      },
      {
        id: "hair_loss_family_answer",
        label: "Nella tua famiglia è comune la caduta dei capelli?",
        options: [
          { value: "no",  label: "No" },
          { value: "yes", label: "Sì" },
        ],
      },
    ],
  },
  {
    title: "Abitudini",
    questions: [
      {
        id: "shower_answer",
        label: "Come valuti la quantità di capelli su pettine / doccia?",
        options: [
          { value: "veryLittle", label: "Pochi" },
          { value: "moderate",   label: "Moderati" },
          { value: "abundant",   label: "Abbondanti" },
        ],
      },
      {
        id: "accessories_answer",
        label: "Indossi spesso accessori?",
        options: [
          { value: "no",  label: "No" },
          { value: "yes", label: "Sì" },
        ],
      },
    ],
  },
  {
    title: "Contesto",
    questions: [
      {
        id: "significant_stress_answer",
        label: "Riporti una condizione di stress significativo?",
        options: [
          { value: "no",  label: "No" },
          { value: "yes", label: "Sì" },
        ],
      },
      {
        id: "age_range_answer",
        label: "Qual è la tua fascia d'età?",
        options: [
          { value: "_18_24",       label: "18-24" },
          { value: "_25_34",       label: "25-34" },
          { value: "_35_44",       label: "35-44" },
          { value: "_45_54",       label: "45-54" },
          { value: "_55_64",       label: "55-64" },
          { value: "_65_and_more", label: "65+" },
        ],
      },
    ],
  },
];

// Default = stessi valori pre-selezionati in ScoreEngine/static/AITricoProtocol.html
// (radio "checked" del pannello Survey), usati per pre-compilare il form.
export const CUTE_QUESTIONNAIRE_DEFAULTS = {
  scalp_oiliness_answer: "normal",
  scalp_hydration_answer: "normal",
  scalp_sensitivity_answer: "no",
  scalp_flaking_answer: "no",
  hair_strength_answer: "normal",
  hair_volume_answer: "normal",
  hair_loss_answer: "no",
  hair_loss_family_answer: "no",
  shower_answer: "moderate",
  accessories_answer: "no",
  significant_stress_answer: "no",
  age_range_answer: "_35_44",
};

export default function CuteQuestionnaire({ value, onChange }) {
  const [open, setOpen] = useState(true);
  const v = value || {};

  const setAnswer = (qid, ans) => {
    const next = { ...v };
    if (next[qid] === ans) {
      // Click sulla stessa risposta → toggle off (la cliente può
      // ripensarci: la domanda torna "senza risposta").
      delete next[qid];
    } else {
      next[qid] = ans;
    }
    onChange?.(next);
  };

  const answered = Object.keys(v).length;
  const total = BLOCKS.reduce((acc, b) => acc + b.questions.length, 0);

  return (
    <section
      className="rounded-2xl border border-[#E5E5E0] bg-white mb-8 animate-fade-up"
      data-testid="cute-questionnaire"
    >
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full p-5 lg:p-6 flex items-center justify-between gap-4 text-left hover:bg-[#FAFAFA] transition-colors rounded-2xl"
        data-testid="cute-questionnaire-toggle"
        aria-expanded={open}
      >
        <div className="flex items-center gap-4">
          <div className="w-11 h-11 rounded-xl bg-[#EAE5DF] flex items-center justify-center shrink-0">
            <ClipboardList className="w-5 h-5 text-[#C5A059]" />
          </div>
          <div>
            <p className="text-xs uppercase tracking-wider text-[#C5A059] font-medium">
              Pre-analisi (opzionale)
            </p>
            <h3 className="font-serif-display text-xl text-[#1A1A1A] mt-0.5">
              Questionario cute
            </h3>
            <p className="text-sm text-[#737373] mt-1">
              Domande per arricchire il contesto della consulenza.
            </p>
          </div>
        </div>
        {open ? (
          <ChevronDown className="w-5 h-5 text-[#737373] shrink-0" />
        ) : (
          <ChevronRight className="w-5 h-5 text-[#737373] shrink-0" />
        )}
      </button>

      {open && (
        <div className="border-t border-[#E5E5E0] p-5 lg:p-6 space-y-8">
          {BLOCKS.map((block) => (
            <div key={block.title}>
              <p className="text-[11px] uppercase tracking-wider text-[#737373] font-semibold mb-4">
                {block.title}
              </p>
              <div className="space-y-5">
                {block.questions.map((q) => (
                  <div key={q.id} data-testid={`cute-q-${q.id}`}>
                    <p className="text-sm text-[#1A1A1A] mb-2.5">{q.label}</p>
                    <div className="flex flex-wrap gap-2">
                      {q.options.map((opt) => {
                        const active = v[q.id] === opt.value;
                        return (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => setAnswer(q.id, opt.value)}
                            className={[
                              "text-sm font-medium transition-colors border rounded-xl",
                              active
                                ? "h-8 px-5 border-[#C5A059] text-[#8A6B2E] bg-[#FAF6EE]"
                                : "h-8 px-5 bg-white text-[#1A1A1A] border-[#E5E5E0] hover:border-[#C5A059] hover:text-[#C5A059]",
                            ].join(" ")}
                            data-testid={`cute-q-${q.id}-opt-${opt.value}`}
                            aria-pressed={active}
                          >
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
